// Pure quantity rules shared by the cart UI.
// The minimum order quantity (MOQ) applies to the PRODUCT total across all its
// variant lines, never to a single variant line.
// A MOQ of 0 means the MOQ is not enforced (cart_enforce_minimum_order_quantity is off).

function sumProductQuantity(cartLines, productId) {
  return cartLines
    .filter((cartLine) => Number(cartLine.productId) === Number(productId))
    .reduce((totalQuantity, cartLine) => totalQuantity + cartLine.quantity, 0);
}

function evaluateProductMinimum(minimumOrderQuantity, productTotalQuantity) {
  if (minimumOrderQuantity <= 0) {
    return { isMet: true, missingQuantity: 0 };
  }

  const missingQuantity = Math.max(0, minimumOrderQuantity - productTotalQuantity);

  return { isMet: missingQuantity === 0, missingQuantity: missingQuantity };
}

// Lowest quantity a single line may be lowered to without dropping the product
// under its MOQ. It never exceeds the line's own quantity, so the plus button
// can never make a line jump up to the MOQ.
function resolveLineQuantityFloor(minimumOrderQuantity, productTotalQuantity, lineQuantity) {
  if (minimumOrderQuantity <= 0) {
    return 0;
  }

  const unitsInOtherLines = productTotalQuantity - lineQuantity;
  const unitsNeededFromThisLine = Math.max(1, minimumOrderQuantity - unitsInOtherLines);

  return Math.min(lineQuantity, unitsNeededFromThisLine);
}

// True when the line is stuck at its floor because of the MOQ: lowering it by one
// unit would leave the product total under the MOQ. A line that is only floored at 1
// while the MOQ is already exceeded (10 + 1 at MOQ 6) is removed through the X, so the
// "minimum order quantity" explanation would be misleading there.
function isLineFloorCausedByMinimum(minimumOrderQuantity, productTotalQuantity) {
  if (minimumOrderQuantity <= 0) {
    return false;
  }

  return productTotalQuantity - 1 < minimumOrderQuantity;
}

// True when lowering a line would leave the product with some units but fewer than
// its MOQ. Removing the whole product (0 units) is always allowed, and raising a line
// is never a removal, even if the product is still under the MOQ afterwards.
function wouldBreakProductMinimum(minimumOrderQuantity, productTotalQuantity, lineQuantity, requestedLineQuantity) {
  if (minimumOrderQuantity <= 1 || requestedLineQuantity >= lineQuantity) {
    return false;
  }

  const resultingProductQuantity = productTotalQuantity - lineQuantity + requestedLineQuantity;

  return resultingProductQuantity > 0 && resultingProductQuantity < minimumOrderQuantity;
}

// Removing a product with several variant lines also removes colors the shopper may
// not see (the group can be collapsed), so the confirmation has to name them.
function resolveRemovalMessageKind(cartLineCount) {
  return cartLineCount > 1 ? 'several-lines' : 'single-line';
}

function resolveMinimumAddQuantity(minimumOrderQuantity, productTotalQuantity) {
  return Math.max(1, minimumOrderQuantity - productTotalQuantity);
}

// Volume discount tiers are fixed at these quantities and evaluated on the PRODUCT
// total (all variant lines). Shared by the PDP and the cart so both agree.
const ALLOWED_VOLUME_DISCOUNT_THRESHOLDS = [4, 8, 12, 20];

function parseVolumeDiscountData(rawVolumeDiscounts) {
  if (rawVolumeDiscounts === null || rawVolumeDiscounts === undefined || rawVolumeDiscounts === '') {
    return null;
  }

  if (typeof rawVolumeDiscounts === 'string') {
    try {
      let parsedVolumeDiscounts = JSON.parse(rawVolumeDiscounts);

      // A text-type metafield rendered with `| json` is encoded twice, so the first parse yields a string.
      if (typeof parsedVolumeDiscounts === 'string') {
        parsedVolumeDiscounts = JSON.parse(parsedVolumeDiscounts);
      }

      if (!parsedVolumeDiscounts || typeof parsedVolumeDiscounts !== 'object' || Array.isArray(parsedVolumeDiscounts)) {
        console.error('Volume discount data must be a JSON object.', parsedVolumeDiscounts);
        return null;
      }

      return parsedVolumeDiscounts;
    } catch (parseError) {
      console.error('Received invalid volume discount JSON.', parseError);
      return null;
    }
  }

  if (typeof rawVolumeDiscounts === 'object') {
    if (Array.isArray(rawVolumeDiscounts)) {
      return null;
    }

    return rawVolumeDiscounts;
  }

  return null;
}

function normalizeVolumeDiscounts(rawVolumeDiscounts) {
  const volumeDiscountData = parseVolumeDiscountData(rawVolumeDiscounts);
  if (!volumeDiscountData) {
    return [];
  }

  const normalizedTiers = [];

  ALLOWED_VOLUME_DISCOUNT_THRESHOLDS.forEach((threshold) => {
    const discountType = volumeDiscountData[`tier_${threshold}_type`];
    const rawDiscountValue = volumeDiscountData[`tier_${threshold}_value`];

    if (discountType !== 'percentage') {
      return;
    }

    const discountValue = Number(rawDiscountValue);
    if (!Number.isFinite(discountValue) || discountValue <= 0 || discountValue > 100) {
      return;
    }

    normalizedTiers.push({
      threshold: threshold,
      value: discountValue
    });
  });

  return normalizedTiers;
}

function findActiveDiscountTier(normalizedTiers, quantity) {
  let activeDiscountTier = null;

  normalizedTiers.forEach((discountTier) => {
    if (discountTier.threshold <= quantity) {
      activeDiscountTier = discountTier;
    }
  });

  return activeDiscountTier;
}

function findNextDiscountTier(normalizedTiers, quantity) {
  return normalizedTiers.find((discountTier) => discountTier.threshold > quantity) || null;
}

// An invalid or empty tier is skipped by normalizeVolumeDiscounts, so the highest
// valid tier at or below the quantity stays active (it "inherits" the previous one).
function describeDiscountProgress(normalizedTiers, productTotalQuantity) {
  const activeTier = findActiveDiscountTier(normalizedTiers, productTotalQuantity);
  const nextTier = findNextDiscountTier(normalizedTiers, productTotalQuantity);
  const missingQuantity = nextTier ? nextTier.threshold - productTotalQuantity : 0;

  return { activeTier: activeTier, nextTier: nextTier, missingQuantity: missingQuantity };
}

// Picks which cart discount message applies and the numbers it needs.
// Business rule: discounts are percentage only and the cart talks in "% off".
// Returns null when there are no tiers, so there is nothing to say.
function describeCartDiscountMessage(normalizedTiers, productTotalQuantity) {
  if (normalizedTiers.length === 0) {
    return null;
  }

  const { activeTier, nextTier, missingQuantity } = describeDiscountProgress(normalizedTiers, productTotalQuantity);
  const activePercentage = activeTier ? activeTier.value : 0;
  const nextPercentage = nextTier ? nextTier.value : 0;

  if (!activeTier) {
    return { kind: 'next-only', missingQuantity: missingQuantity, activePercentage: activePercentage, nextPercentage: nextPercentage };
  }

  if (!nextTier) {
    return { kind: 'applied', missingQuantity: missingQuantity, activePercentage: activePercentage, nextPercentage: nextPercentage };
  }

  return { kind: 'applied-and-next', missingQuantity: missingQuantity, activePercentage: activePercentage, nextPercentage: nextPercentage };
}
